import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiPlus, FiDownload, FiAlertTriangle, FiPackage, FiEdit3, FiEye } = FiIcons;

// Dark theme status styles
const STATUS_COLORS = {
  healthy: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
  low: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  critical: 'bg-red-500/10 text-red-400 border-red-500/30'
};

const Inventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const inventoryData = [
    {
      id: 1,
      sku: 'APL-001',
      name: 'Granny Smith Apples',
      category: 'Fruits',
      currentStock: 450,
      minThreshold: 200,
      maxThreshold: 1000,
      unitPrice: 15.50,
      costPrice: 12.00,
      supplier: 'Fresh Farms Ltd',
      lastUpdated: '2024-01-15',
      status: 'healthy'
    },
    {
      id: 2,
      sku: 'ORG-023',
      name: 'Navel Oranges',
      category: 'Fruits',
      currentStock: 180,
      minThreshold: 200,
      maxThreshold: 800,
      unitPrice: 18.00,
      costPrice: 14.50,
      supplier: 'Citrus Co',
      lastUpdated: '2024-01-15',
      status: 'low'
    },
    {
      id: 3,
      sku: 'BAN-012',
      name: 'Cavendish Bananas',
      category: 'Fruits',
      currentStock: 75,
      minThreshold: 150,
      maxThreshold: 600,
      unitPrice: 12.00,
      costPrice: 9.00,
      supplier: 'Tropical Imports',
      lastUpdated: '2024-01-14',
      status: 'critical'
    },
    {
      id: 4,
      sku: 'TOM-045',
      name: 'Beef Tomatoes',
      category: 'Vegetables',
      currentStock: 320,
      minThreshold: 100,
      maxThreshold: 500,
      unitPrice: 25.00,
      costPrice: 18.00,
      supplier: 'Garden Fresh',
      lastUpdated: '2024-01-15',
      status: 'healthy'
    },
    {
      id: 5,
      sku: 'LET-078',
      name: 'Iceberg Lettuce',
      category: 'Vegetables',
      currentStock: 95,
      minThreshold: 80,
      maxThreshold: 300,
      unitPrice: 8.50,
      costPrice: 6.00,
      supplier: 'Local Greens',
      lastUpdated: '2024-01-15',
      status: 'healthy'
    }
  ];

  const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.healthy;

  const filteredData = inventoryData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const summaryStats = {
    total: inventoryData.length,
    critical: inventoryData.filter(item => item.status === 'critical').length,
    low: inventoryData.filter(item => item.status === 'low').length,
    healthy: inventoryData.filter(item => item.status === 'healthy').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9] tracking-tight">
            <span className="text-[#84cc16]">Inventory</span> Control
          </h1>
          <p className="text-[#6b8772] mt-1 font-mono text-sm">Stock levels, pricing, and procurement management</p>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#111916] border border-[#243129] text-[#a5c4ad] px-4 py-2 rounded-lg hover:border-[#84cc16]/30 transition-colors flex items-center gap-2 font-mono text-sm"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4" />
            <span>Export</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-2 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow flex items-center gap-2 font-mono text-sm font-medium"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Item</span>
          </motion.button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#84cc16]/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Total Items</p>
              <p className="text-2xl font-display font-semibold text-[#e8f5e9] mt-1">{summaryStats.total}</p>
            </div>
            <div className="bg-sky-500/10 rounded-xl p-3 border border-sky-500/20">
              <SafeIcon icon={FiPackage} className="w-5 h-5 text-sky-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-red-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Critical Stock</p>
              <p className="text-2xl font-display font-semibold text-red-400 mt-1">{summaryStats.critical}</p>
            </div>
            <div className="bg-red-500/10 rounded-xl p-3 border border-red-500/20">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-amber-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Low Stock</p>
              <p className="text-2xl font-display font-semibold text-amber-400 mt-1">{summaryStats.low}</p>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-3 border border-amber-500/20">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-lime-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider">Healthy Stock</p>
              <p className="text-2xl font-display font-semibold text-lime-400 mt-1">{summaryStats.healthy}</p>
            </div>
            <div className="bg-lime-500/10 rounded-xl p-3 border border-lime-500/20">
              <SafeIcon icon={FiPackage} className="w-5 h-5 text-lime-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-[#111916] rounded-xl p-5 border border-[#243129]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative group">
            <SafeIcon icon={FiSearch} className="w-4 h-4 text-[#6b8772] absolute left-3 top-1/2 transform -translate-y-1/2 group-focus-within:text-[#84cc16] transition-colors" />
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[#0a0f0d] border border-[#243129] rounded-lg text-[#e8f5e9] placeholder-[#4a5f50] font-mono text-sm w-80 focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[rgba(132,204,22,0.2)] transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="w-4 h-4 text-[#6b8772]" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#0a0f0d] border border-[#243129] rounded-lg px-3 py-2 text-[#a5c4ad] font-mono text-sm focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[rgba(132,204,22,0.2)] transition-all"
            >
              <option value="all">All Status</option>
              <option value="healthy">Healthy</option>
              <option value="low">Low Stock</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-[#111916] rounded-xl border border-[#243129] overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-[#0a0f0d]">
              <tr>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Stock Status</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Pricing</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Supplier</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Last Updated</th>
                <th className="px-6 py-3 text-right text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1c2822]">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-[#1a2420] transition-colors group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-mono font-medium text-[#e8f5e9] group-hover:text-[#84cc16] transition-colors">{item.name}</div>
                      <div className="text-xs font-mono text-[#4a5f50]">{item.sku} • {item.category}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    <div className="text-xs font-mono text-[#6b8772] mt-1">
                      {item.currentStock} / {item.maxThreshold} units
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-mono font-medium text-[#e8f5e9]">R {item.unitPrice}</div>
                    <div className="text-xs font-mono text-[#4a5f50]">Cost: R {item.costPrice}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-mono text-[#a5c4ad]">{item.supplier}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-[#6b8772]">
                    {item.lastUpdated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="text-[#6b8772] hover:text-[#84cc16] p-1.5 hover:bg-lime-500/10 rounded-md transition-colors">
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      <button className="text-[#6b8772] hover:text-sky-400 p-1.5 hover:bg-sky-500/10 rounded-md transition-colors">
                        <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Inventory;
